import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { COLORS } from "../../common/colors";
import type {
  PatientRow,
  StreamCoverage
} from "src/providers/compliance/complianceTypes";
import {
  CONTACT_LABELS,
  STREAM_DISPLAY
} from "src/providers/compliance/complianceTypes";
import type {
  MissingDataStream,
  TriageConfig
} from "src/providers/compliance/complianceFilters";
import {
  daysMonitored,
  isExempt,
  isNewPatient,
  NEW_PATIENT_DAYS,
  runComplianceCheck,
  isAwaitingPro,
  recencyFor
} from "src/providers/compliance/complianceFilters";
import { ExemptLight, recencyLabel, TrafficLight } from "./TrafficLight";
import { MedCell } from "./MedCell";
import {
  CB,
  checkSwatchLevel,
  COMPLIANCE_COLORS,
  FOCUSED_COLUMN_SX
} from "./complianceColors";
import { COLUMN_WIDTHS, STREAM_MIN_WIDTH } from "./complianceLayout";

interface PatientListRowProps {
  patient: PatientRow;
  onClick: (patientId: string) => void;
  selected: boolean;
  visibleStreams: MissingDataStream[];
  focusedStream: MissingDataStream | null;
  triageConfig: TriageConfig;
  // The time triage ran at, so cells count the same days as their triage group.
  now: Date;
}

const CELL_BORDER_LEFT = `1px solid ${COMPLIANCE_COLORS.BORDER_ROW}`;

const STREAM_CELL_SX = {
  flex: 1,
  minWidth: STREAM_MIN_WIDTH,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  alignSelf: "stretch",
  py: "6px"
} as const;

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];

function findStream(
  streams: StreamCoverage[],
  name: string
): StreamCoverage | undefined {
  return streams.find((s) => s.name === name);
}

function contactLabel(patient: PatientRow): string {
  const { state, gapDays } = patient.contact;
  if (state === "none" || state === "exhausted" || state === "unknown") {
    return CONTACT_LABELS[state];
  }
  if (gapDays === null) return CONTACT_LABELS.unknown;
  if (gapDays === 0) return "today";
  return `${gapDays}d`;
}

function StreamCell({
  patient,
  streamName,
  triageConfig,
  now
}: {
  patient: PatientRow;
  streamName: MissingDataStream;
  triageConfig: TriageConfig;
  now: Date;
}): JSX.Element {
  const stream = findStream(patient.streams, streamName);
  const label = STREAM_DISPLAY[streamName]?.long ?? streamName;
  if (isExempt(patient, streamName)) {
    return (
      <ExemptLight
        label={label}
        reason={stream?.exemptWhy ?? patient.exemption?.reason ?? null}
      />
    );
  }
  const check = runComplianceCheck(patient, streamName, triageConfig, now);
  return (
    <TrafficLight
      level={checkSwatchLevel(check)}
      label={label}
      caption={
        isAwaitingPro(patient, streamName)
          ? "—"
          : recencyLabel(
              streamName === "medication"
                ? undefined
                : recencyFor(patient, streamName)?.daysSinceLastData,
              daysMonitored(patient, now)
            )
      }
      status={isAwaitingPro(patient, streamName) ? "not available" : undefined}
    />
  );
}

function formatDateWithYear(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

export const PatientListRow = React.memo(function PatientListRow({
  patient,
  onClick,
  selected,
  visibleStreams,
  focusedStream,
  triageConfig,
  now
}: PatientListRowProps): JSX.Element {
  const isNew = isNewPatient(patient, now);
  return (
    <Box
      role="row"
      // Lets the sidebar return focus to this row when it closes.
      data-patient-row={patient.patientId}
      // aria-selected is only defined for grids; aria-current is valid on a table row.
      aria-current={selected ? "true" : undefined}
      tabIndex={0}
      onClick={() => onClick(patient.patientId)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(patient.patientId);
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        borderBottom: `1px solid ${COMPLIANCE_COLORS.BORDER_ROW}`,
        cursor: "pointer",
        // Inset accent rather than a border, so the row's columns don't shift.
        ...(selected && {
          backgroundColor: COLORS.PRIMARY_200,
          boxShadow: `inset 3px 0 0 ${COLORS.PRIMARY_1000}`
        }),
        "&:hover": {
          backgroundColor: selected
            ? COLORS.PRIMARY_300
            : COMPLIANCE_COLORS.HOVER_BG
        }
      }}
    >
      {/* Patient column */}
      <Box
        role="cell"
        sx={{
          width: COLUMN_WIDTHS.patient,
          flexShrink: 0,
          minWidth: 0,
          px: "14px",
          py: "9px"
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: COLORS.PRIMARY_1500,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
        >
          {patient.codeName}
        </Typography>
        {patient.triageReasons.length > 0 && (
          // One reason per line, wrapping within the cell if a line is still too long.
          <Box
            sx={{
              display: "inline-flex",
              flexDirection: "column",
              gap: "3px",
              maxWidth: "100%",
              mt: "3px",
              px: "6px",
              py: "3px",
              borderRadius: "4px",
              backgroundColor: COMPLIANCE_COLORS.ROW_ALT_BG,
              fontSize: 10.5,
              fontWeight: 500,
              color: COLORS.GREY_700,
              lineHeight: 1.2
            }}
          >
            {patient.triageReasons.map((reason) => (
              <span key={reason}>{reason}</span>
            ))}
          </Box>
        )}
        {patient.proLoaded && patient.timezoneMissing && (
          <Typography
            sx={{
              mt: "3px",
              fontSize: 10.5,
              fontWeight: 500,
              color: COLORS.GREY_600,
              lineHeight: 1.2
            }}
          >
            No time zone, days counted in UTC
          </Typography>
        )}
      </Box>

      {/* App mode column */}
      <Box
        role="cell"
        sx={{
          width: COLUMN_WIDTHS.appMode,
          flexShrink: 0,
          px: "12px",
          py: "9px",
          borderLeft: CELL_BORDER_LEFT
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: patient.appMode ? COLORS.GREY_800 : COLORS.GREY_500,
            whiteSpace: "nowrap"
          }}
        >
          {patient.appMode ?? "—"}
        </Typography>
      </Box>

      {/* Start date column */}
      <Box
        role="cell"
        sx={{
          width: COLUMN_WIDTHS.startDate,
          flexShrink: 0,
          px: "12px",
          py: "9px",
          borderLeft: CELL_BORDER_LEFT
        }}
      >
        <Typography
          sx={{
            fontSize: 11.5,
            fontWeight: 500,
            color: COLORS.GREY_800,
            lineHeight: 1.3,
            whiteSpace: "nowrap"
          }}
        >
          {formatDateWithYear(patient.window.start)}
        </Typography>
      </Box>

      {/* Created column */}
      <Box
        role="cell"
        sx={{
          width: COLUMN_WIDTHS.createdAt,
          flexShrink: 0,
          px: "12px",
          py: "9px",
          borderLeft: CELL_BORDER_LEFT
        }}
      >
        {isNew ? (
          <Tooltip
            title={`New patient — created within the last ${NEW_PATIENT_DAYS} days`}
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  fontSize: 12,
                  fontWeight: 500,
                  fontFamily: "Work Sans, sans-serif"
                }
              }
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                flexDirection: "column",
                px: "6px",
                py: "3px",
                borderRadius: "6px",
                border: `1px solid ${CB.SKY}`,
                backgroundColor: CB.SKY_BG
              }}
            >
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: CB.SKY_TEXT,
                  lineHeight: 1.3
                }}
              >
                {formatDateWithYear(patient.createdAt)}
              </Typography>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: CB.SKY_TEXT,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  lineHeight: 1.2
                }}
              >
                New
              </Typography>
            </Box>
          </Tooltip>
        ) : (
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 500,
              color: COLORS.GREY_800,
              lineHeight: 1.3,
              whiteSpace: "nowrap"
            }}
          >
            {formatDateWithYear(patient.createdAt)}
          </Typography>
        )}
      </Box>

      {/* Last contact column */}
      <Box
        role="cell"
        sx={{
          width: COLUMN_WIDTHS.lastContact,
          flexShrink: 0,
          px: "12px",
          py: "13px",
          borderLeft: CELL_BORDER_LEFT
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: COLORS.GREY_800
          }}
        >
          {contactLabel(patient)}
        </Typography>
      </Box>

      {/* Medication column */}
      {visibleStreams.includes("medication") && (
        <Box
          role="cell"
          sx={{
            width: COLUMN_WIDTHS.medication,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            alignSelf: "stretch",
            px: "12px",
            py: "9px",
            borderLeft: CELL_BORDER_LEFT,
            ...(focusedStream === "medication" ? FOCUSED_COLUMN_SX : {})
          }}
        >
          <MedCell
            medRecent={patient.medRecent}
            loaded={patient.proLoaded}
            check={runComplianceCheck(patient, "medication", triageConfig, now)}
            monitoredDays={daysMonitored(patient, now)}
            exempt={isExempt(patient, "medication")}
            exemptReason={patient.exemption?.reason ?? null}
          />
        </Box>
      )}

      {/* Stream columns — each flex: 1 */}
      {visibleStreams.some((s) => s !== "medication") && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignSelf: "stretch",
            borderLeft: CELL_BORDER_LEFT
          }}
        >
          {visibleStreams
            .filter((s) => s !== "medication")
            .map((streamName) => (
              <Box
                key={streamName}
                role="cell"
                sx={{
                  ...STREAM_CELL_SX,
                  ...(focusedStream === streamName ? FOCUSED_COLUMN_SX : {})
                }}
              >
                <StreamCell
                  patient={patient}
                  streamName={streamName}
                  triageConfig={triageConfig}
                  now={now}
                />
              </Box>
            ))}
        </Box>
      )}
    </Box>
  );
});
